import React, { useState } from 'react'
import { API_BASE } from '../supabaseClient'

const DEFAULT_REQUEST = {
  external_id: 'bank-ref-001',
  input_type: 'forecast',
  raw_ai_output: {
    prediction: 1250000,
    confidence: 0.87,
    explanation: 'Based on historical trends and macro indicators',
    model_name: 'claude-4.6',
    timestamp: new Date().toISOString(),
  },
  context: {
    target_variable: 'Q3 CPG sales',
    forecast_horizon_months: 3,
    granularity: 'monthly',
    vertical: 'cpg',
  },
  customer_metadata: { department: 'treasury' },
}

export default function Validate({ session }) {
  const [requestBody, setRequestBody] = useState(JSON.stringify(DEFAULT_REQUEST, null, 2))
  const [response, setResponse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    setResponse(null)

    try {
      const parsed = JSON.parse(requestBody)
      const token = session?.access_token
      const headers = { 'Content-Type': 'application/json' }
      if (token) headers.Authorization = `Bearer ${token}`

      const res = await fetch(`${API_BASE}/v1/validation/validate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(parsed),
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error?.message || 'Validation failed')
      setResponse(data)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Submit Validation</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Request</h2>
          <textarea
            value={requestBody}
            onChange={(e) => setRequestBody(e.target.value)}
            className="w-full h-96 font-mono text-sm p-3 border rounded-md"
          />
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="mt-4 w-full py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Validating...' : 'Submit for Validation'}
          </button>
          {error && (
            <div className="mt-4 p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Response</h2>
          {response ? (
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  response.status === 'validated' ? 'bg-green-100 text-green-700' :
                  response.status === 'human_review_required' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {response.status}
                </span>
                {response.audit_ready && (
                  <span className="px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700">
                    Audit Ready
                  </span>
                )}
              </div>

              <pre className="bg-gray-50 p-4 rounded-md overflow-auto text-xs font-mono h-72">
                {JSON.stringify(response, null, 2)}
              </pre>

              {response.audit_ready && response.validation_id && (
                <a
                  href={`${API_BASE}/v1/audit/${response.validation_id}.zip`}
                  className="mt-4 inline-block px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  download
                >
                  Download Audit Package
                </a>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Submit a validation request to see the response.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
